import { useForm } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { admin, brandsQuery, numbersQuery, qk, usersQuery } from "../../../api/admin";
import { errorMessage } from "../../../api/client";
import { textingQuery } from "../../../api/messaging";
import {
  addressesQuery,
  type NumberOrder,
  nk,
  ordersQuery,
  purchaseSettingsQuery,
} from "../../../api/numbers";
import type { AdminUser, Brand, NumberMember, PhoneNumber } from "../../../api/types";
import { rolesOf, useCanEdit } from "../../../auth/useMe";
import { DataTable } from "../../../components/DataTable";
import { NumberPeople, peopleSummary } from "../../../components/NumberPeople";
import {
  EmergencyCell,
  EmergencyPicker,
  OrderBadge,
  OrderEmergencyCell,
  TextingCell,
} from "../../../components/numbers/Cells";
import { GetNumberSheet } from "../../../components/numbers/GetNumberSheet";
import { OrderDialog } from "../../../components/numbers/OrderDialog";
import { ReleaseDialog } from "../../../components/numbers/ReleaseDialog";
import { BringNumbersSheet } from "../../../components/ports/BringNumbersSheet";
import { PortsTab } from "../../../components/ports/PortsTab";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Spinner,
  Tabs,
} from "../../../components/ui";
import { fmtPhone } from "../../../lib/format";
import { firstError } from "../../../lib/forms";
import { useNavigate } from "../../../lib/navigate";
import { allowanceCaption, numberInMotion, onlyNumberWith911, orderRows } from "../../../lib/numbers";

const searchSchema = z.object({
  tab: z.enum(["numbers", "brands", "ports"]).optional().catch(undefined),
  brand: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/numbers/")({
  staticData: { crumb: { label: "Numbers", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(numbersQuery),
      context.queryClient.prefetchQuery(brandsQuery),
      context.queryClient.prefetchQuery(ordersQuery),
      context.queryClient.prefetchQuery(purchaseSettingsQuery),
    ]),
  component: NumbersPage,
});

function NumbersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab = search.tab ?? "numbers";
  return (
    <>
      <PageHeader title="Numbers and brands" />
      <Tabs
        label="Numbers and brands"
        value={tab}
        onChange={(t) =>
          navigate({
            search: (p) => ({ ...p, tab: t === "numbers" ? undefined : t }),
            replace: true,
          })
        }
        tabs={[
          { value: "numbers", label: "Numbers" },
          { value: "brands", label: "Brands" },
          { value: "ports", label: "Ports" },
        ]}
      />
      {tab === "numbers" ? (
        <NumbersTab brandFilter={search.brand} />
      ) : tab === "ports" ? (
        <PortsTab />
      ) : (
        <BrandsTab />
      )}
    </>
  );
}

const DISMISSED = "kite.numbers.dismissedOrders";

function readDismissed(): ReadonlySet<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** Remembers a dismissed failed order (the last 50) and returns the new set. */
function dismiss(id: string): ReadonlySet<string> {
  const ids = [...readDismissed(), id].slice(-50);
  try {
    localStorage.setItem(DISMISSED, JSON.stringify(ids));
  } catch {
    // Private mode: the row comes back on reload, nothing else breaks.
  }
  return new Set(ids);
}

/** A row of the Numbers table: an open order at the top, or a number. */
type Row = { kind: "order"; order: NumberOrder } | { kind: "number"; number: PhoneNumber };

const ncol = createColumnHelper<Row>();

function NumbersTab({ brandFilter }: { brandFilter: string | undefined }) {
  const canEdit = useCanEdit();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const orders = useQuery(ordersQuery);
  const moving = (orders.data ?? []).some((o) => o.status === "buying" || o.status === "setting_up");
  // Poll while a row is still changing (911 turning on, a release, an order joining), and stop
  // when none is.
  const numbers = useQuery({
    ...numbersQuery,
    refetchInterval: (q) => (moving || q.state.data?.some(numberInMotion) ? 5_000 : false),
  });
  const brands = useQuery(brandsQuery);
  const users = useQuery(usersQuery);
  const settings = useQuery(purchaseSettingsQuery);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(readDismissed);
  // An order that left Buying or Setting up changed the numbers and the allowance.
  const openIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!orders.data) return;
    const now = new Set(
      orders.data.filter((o) => o.status === "buying" || o.status === "setting_up").map((o) => o.id),
    );
    const before = openIds.current;
    openIds.current = now;
    if (before && [...before].some((id) => !now.has(id))) {
      void qc.invalidateQueries({ queryKey: qk.numbers });
      void qc.invalidateQueries({ queryKey: nk.settings });
    }
  }, [orders.data, qc]);
  // Addresses are admin-only on the server; managers see 911 without the address name.
  const addresses = useQuery({ ...addressesQuery, enabled: canEdit });
  const texting = useQuery(textingQuery);
  const [editing, setEditing] = useState<PhoneNumber | "new" | null>(null);
  const [getting, setGetting] = useState(false);
  const [bringing, setBringing] = useState(false);
  const [openOrder, setOpenOrder] = useState<NumberOrder | null>(null);
  const [adding911, setAdding911] = useState<PhoneNumber | null>(null);
  const [releasing, setReleasing] = useState<PhoneNumber | null>(null);
  const brandName = useCallback(
    (id: string | null) => brands.data?.find((b) => b.id === id)?.name ?? (id ? "" : "Company-wide"),
    [brands.data],
  );
  const userName = useCallback(
    (id: string | null) => {
      const user = users.data?.find((u) => u.id === id);
      return user ? memberLabel(user) : "";
    },
    [users.data],
  );
  const textingOf = useCallback(
    (e164: string) => texting.data?.status.numbers.find((n) => n.e164 === e164)?.state,
    [texting.data],
  );

  // The table caches each row's cell values until its data changes, so a fresh array when the
  // users, brands, addresses or texting arrive lets those columns pick up their values.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the lookups are the point
  const rows = useMemo<Row[]>(
    () => [
      ...orderRows(orders.data ?? [], dismissed, Date.now())
        .filter((o) => !brandFilter || o.brand_id === brandFilter)
        .map((order) => ({ kind: "order" as const, order })),
      ...(numbers.data ?? [])
        .filter((n) => !brandFilter || n.brand_id === brandFilter)
        .map((number) => ({ kind: "number" as const, number })),
    ],
    [
      orders.data,
      dismissed,
      numbers.data,
      brandFilter,
      brandName,
      userName,
      addresses.data,
      textingOf,
      settings.data,
    ],
  );
  const columns = useMemo(
    () => [
      ncol.accessor((r) => (r.kind === "order" ? r.order.e164 : r.number.e164), {
        id: "e164",
        header: "Number",
        cell: (c) => <span className="whitespace-nowrap font-medium tnum">{fmtPhone(c.getValue())}</span>,
      }),
      ncol.accessor((r) => (r.kind === "order" ? r.order.label : r.number.label), {
        id: "label",
        header: "Label",
      }),
      ncol.accessor((r) => brandName(r.kind === "order" ? r.order.brand_id : r.number.brand_id), {
        id: "brand",
        header: "Brand",
      }),
      ncol.accessor((r) => (r.kind === "number" ? userName(r.number.owner_user_id) : ""), {
        id: "owner",
        header: "Owner",
        cell: (c) => {
          const r = c.row.original;
          if (r.kind === "order") return <OrderBadge order={r.order} />;
          if (r.number.state === "releasing")
            return (
              <Badge tone="neutral" className="gap-1">
                <Spinner className="size-3" />
                Releasing
              </Badge>
            );
          return c.getValue() || <span className="text-ink-3">Unassigned</span>;
        },
      }),
      ncol.accessor((r) => (r.kind === "number" ? peopleSummary(r.number.members.map((m) => m.name)) : ""), {
        id: "members",
        header: "Shared with",
        cell: (c) => {
          const r = c.row.original;
          return c.getValue() && r.kind === "number" ? (
            <span title={r.number.members.map((m) => m.name).join(", ")}>{c.getValue()}</span>
          ) : null;
        },
      }),
      ncol.display({
        id: "e911",
        header: "911",
        cell: (c) => {
          const r = c.row.original;
          return r.kind === "order" ? (
            <OrderEmergencyCell order={r.order} addresses={addresses.data} />
          ) : (
            <EmergencyCell
              number={r.number}
              addresses={addresses.data}
              canEdit={canEdit}
              settings={settings.data}
              onAdd={setAdding911}
            />
          );
        },
      }),
      ncol.display({
        id: "texting",
        header: "Texting",
        cell: (c) => {
          const r = c.row.original;
          return r.kind === "number" ? <TextingCell state={textingOf(r.number.e164)} /> : null;
        },
      }),
    ],
    [brandName, userName, addresses.data, canEdit, settings.data, textingOf],
  );
  const caption = allowanceCaption(settings.data);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          aria-label="Brand"
          className="w-44"
          value={brandFilter ?? ""}
          onValueChange={(v) =>
            navigate({
              search: (p) => ({ ...p, brand: v || undefined }),
              replace: true,
            })
          }
          options={[
            { value: "", label: "All brands" },
            ...(brands.data ?? []).map((b) => ({ value: b.id, label: b.name })),
          ]}
        />
        {canEdit ? (
          <>
            <Button className="ml-auto" onClick={() => setEditing("new")}>
              Add number
            </Button>
            <Button onClick={() => setBringing(true)}>Bring numbers</Button>
            <Button variant="primary" onClick={() => setGetting(true)}>
              <Plus aria-hidden />
              Get a number
            </Button>
          </>
        ) : null}
      </div>
      <Card>
        {numbers.isPending ? (
          <Loading />
        ) : numbers.isError ? (
          <ErrorState error={numbers.error} onRetry={() => numbers.refetch()} />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(r) => (r.kind === "order" ? `order-${r.order.id}` : r.number.id)}
            onRowClick={(r: Row) => {
              if (r.kind === "order") setOpenOrder(r.order);
              else if (canEdit) setEditing(r.number);
            }}
            rowLabel={(r: Row) =>
              r.kind === "order"
                ? `Open the order for ${fmtPhone(r.order.e164)}`
                : `Edit ${fmtPhone(r.number.e164)}`
            }
            empty={
              <EmptyState
                title="No numbers yet"
                description="Add numbers your company already owns so reps can call from them."
              />
            }
          />
        )}
      </Card>
      {caption ? <p className="mx-1 mt-2.5 text-caption text-ink-3">{caption}</p> : null}
      {editing ? (
        <NumberDialog
          number={editing === "new" ? null : editing}
          brands={brands.data ?? []}
          users={users.data ?? []}
          canRelease={
            editing !== "new" && Boolean(editing.order_id) && (settings.data?.purchase_enabled ?? false)
          }
          onRelease={(n) => {
            setEditing(null);
            setReleasing(n);
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {canEdit ? (
        <GetNumberSheet
          open={getting}
          onClose={() => setGetting(false)}
          settings={settings.data}
          brands={brands.data ?? []}
          users={users.data ?? []}
        />
      ) : null}
      {canEdit ? <BringNumbersSheet open={bringing} onClose={() => setBringing(false)} /> : null}
      {openOrder ? (
        <OrderDialog
          order={openOrder}
          addresses={addresses.data ?? []}
          canEdit={canEdit}
          onClose={() => {
            // Closing a failed order dismisses its row.
            if (
              openOrder.status === "failed" ||
              qc.getQueryData<NumberOrder>(nk.order(openOrder.id))?.status === "failed"
            )
              setDismissed(dismiss(openOrder.id));
            setOpenOrder(null);
          }}
          onGetAnother={() => {
            setDismissed(dismiss(openOrder.id));
            setOpenOrder(null);
            setGetting(true);
          }}
        />
      ) : null}
      {adding911 ? (
        <EmergencyPicker number={adding911} settings={settings.data} onClose={() => setAdding911(null)} />
      ) : null}
      {releasing ? (
        <ReleaseDialog
          number={releasing}
          ownerName={userName(releasing.owner_user_id) || null}
          only911={onlyNumberWith911(releasing, numbers.data ?? [])}
          onClose={() => setReleasing(null)}
        />
      ) : null}
    </>
  );
}

const numberSchema = z.object({
  e164: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/, "Use E.164 format, like +18165551234."),
  label: z.string().max(60, "Keep the label under 60 characters."),
  brand_id: z.string(),
  owner_user_id: z.string(),
});

function memberLabel(user: AdminUser): string {
  return user.name.trim() || user.email;
}

function NumberDialog({
  number,
  brands,
  users,
  canRelease,
  onRelease,
  onClose,
}: {
  number: PhoneNumber | null;
  brands: Brand[];
  users: AdminUser[];
  /** Kite bought it and purchasing is open: the footer offers Release number. */
  canRelease: boolean;
  onRelease: (n: PhoneNumber) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const [serverError, setServerError] = useState<string | null>(null);
  // People are edited here and saved with everything else; Cancel leaves the number as it was.
  const [people, setPeople] = useState<NumberMember[]>(number?.members ?? []);
  const form = useForm({
    defaultValues: {
      e164: number?.e164 ?? "",
      label: number?.label ?? "",
      brand_id: number?.brand_id ?? "",
      owner_user_id: number?.owner_user_id ?? "",
    },
    validators: { onSubmit: numberSchema },
    onSubmit: async ({ value }) => {
      setServerError(null);
      const body = {
        label: value.label,
        brand_id: value.brand_id || null,
        owner_user_id: value.owner_user_id || null,
      };
      try {
        let saved = number
          ? await admin.patchNumber(number.id, body)
          : await admin.createNumber({ e164: value.e164.trim(), ...body });
        if (number) {
          const before = new Set(number.members.map((m) => m.user_id));
          const after = new Set(people.map((m) => m.user_id));
          for (const m of number.members)
            if (!after.has(m.user_id)) await admin.removeNumberMember(number.id, m.user_id);
          for (const m of people)
            if (!before.has(m.user_id)) await admin.addNumberMember(number.id, m.user_id);
          saved = { ...saved, members: people };
        }
        qc.setQueryData<PhoneNumber[]>(qk.numbers, (l) =>
          number ? l?.map((n) => (n.id === saved.id ? saved : n)) : [...(l ?? []), saved],
        );
        qc.invalidateQueries({ queryKey: qk.users });
        toast(number ? "Number saved." : `${fmtPhone(saved.e164)} added.`);
        onClose();
      } catch (e) {
        setServerError(errorMessage(e));
      }
    },
  });
  return (
    <Dialog open onClose={onClose} title={number ? fmtPhone(number.e164) : "Add number"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        {number ? null : (
          <form.Field name="e164">
            {(f) => (
              <Field label="Number" error={firstError(f.state.meta.errors)}>
                <Input
                  id="n-e164"
                  autoFocus
                  placeholder="+18165551234"
                  value={f.state.value}
                  onChange={(e) => f.handleChange(e.target.value)}
                  onBlur={f.handleBlur}
                />
              </Field>
            )}
          </form.Field>
        )}
        <form.Field name="label">
          {(f) => (
            <Field label="Label" error={firstError(f.state.meta.errors)}>
              <Input
                id="n-label"
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value)}
                onBlur={f.handleBlur}
              />
            </Field>
          )}
        </form.Field>
        <div className="grid grid-cols-2 gap-3">
          <form.Field name="brand_id">
            {(f) => (
              <Field label="Brand" error={firstError(f.state.meta.errors)}>
                <Select
                  id="n-brand"
                  value={f.state.value}
                  onValueChange={(v) => f.handleChange(v)}
                  options={[
                    { value: "", label: "Company-wide" },
                    ...brands.map((b) => ({ value: b.id, label: b.name })),
                  ]}
                />
              </Field>
            )}
          </form.Field>
          <form.Field name="owner_user_id">
            {(f) => (
              <Field
                label="Owner"
                help="The owner can use this number. Without an owner, inbound calls follow the number's call flow or ring group."
              >
                <Select
                  value={f.state.value}
                  onValueChange={(v) => f.handleChange(v)}
                  options={[
                    { value: "", label: "Unassigned" },
                    // Numbers belong to reps: a manager without Rep has no phone.
                    ...users
                      .filter((u) => u.status !== "disabled" && (rolesOf(u).rep || u.id === f.state.value))
                      .map((u) => ({ value: u.id, label: memberLabel(u) })),
                  ]}
                />
              </Field>
            )}
          </form.Field>
        </div>
        <Field
          label="Carrier caller ID"
          help="Adding or editing a number in Kite does not update its carrier caller-name listing."
        >
          <span className="text-ui text-ink-2">Managed by your phone service provider</span>
        </Field>
        {number ? (
          <form.Subscribe selector={(st) => st.values.owner_user_id}>
            {(ownerId) => {
              const owner = users.find((u) => u.id === ownerId);
              return (
                <NumberPeople
                  owner={owner ? { id: owner.id, name: memberLabel(owner) } : null}
                  members={people.filter((m) => m.user_id !== ownerId)}
                  candidates={users
                    .filter((u) => u.status === "active" && rolesOf(u).rep)
                    .map((u) => ({ ...u, name: memberLabel(u) }))}
                  canEdit={canEdit}
                  onAdd={async (userId) => {
                    const u = users.find((x) => x.id === userId);
                    if (u) setPeople([...people, { user_id: u.id, name: memberLabel(u), email: u.email }]);
                  }}
                  onRemove={async (userId) => {
                    setPeople(people.filter((m) => m.user_id !== userId));
                  }}
                />
              );
            }}
          </form.Subscribe>
        ) : null}
        {serverError ? (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-ui text-danger-ink">
            {serverError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          {number && canRelease ? (
            <Button variant="ghost" className="mr-auto text-danger-ink" onClick={() => onRelease(number)}>
              Release number
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(busy) => (
              <Button type="submit" variant="primary" busy={busy}>
                {number ? "Save" : "Add number"}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </Dialog>
  );
}

const bcol = createColumnHelper<Brand & { count: number }>();

function BrandsTab() {
  const canEdit = useCanEdit();
  const brands = useQuery(brandsQuery);
  const numbers = useQuery(numbersQuery);
  const [editing, setEditing] = useState<Brand | "new" | null>(null);
  const rows = useMemo(
    () =>
      (brands.data ?? []).map((b) => ({
        ...b,
        count: (numbers.data ?? []).filter((n) => n.brand_id === b.id).length,
      })),
    [brands.data, numbers.data],
  );
  const columns = useMemo(
    () => [
      bcol.accessor("name", {
        header: "Brand",
        cell: (c) => <span className="font-medium">{c.getValue()}</span>,
      }),
      bcol.accessor("caller_name", {
        header: "Caller name",
        cell: (c) => <span className="font-mono text-caption">{c.getValue()}</span>,
      }),
      bcol.accessor("messaging_profile_id", {
        header: "Texting profile",
        cell: (c) =>
          c.getValue() ? (
            <span className="font-mono text-caption">{c.getValue()}</span>
          ) : (
            <span className="text-ink-3">Not set up</span>
          ),
      }),
      bcol.accessor("count", { header: "Numbers", meta: { align: "right" } }),
    ],
    [],
  );
  return (
    <>
      {canEdit ? (
        <div className="mb-3 flex justify-end">
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add brand
          </Button>
        </div>
      ) : null}
      <Card>
        {brands.isPending ? (
          <Loading />
        ) : brands.isError ? (
          <ErrorState error={brands.error} onRetry={() => brands.refetch()} />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(b) => b.id}
            {...(canEdit
              ? {
                  onRowClick: (b: Brand) => setEditing(b),
                  rowLabel: (b: Brand) => `Edit ${b.name}`,
                }
              : {})}
            empty={<EmptyState title="No brands yet" />}
          />
        )}
      </Card>
      {editing ? (
        <BrandDialog brand={editing === "new" ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

const brandSchema = z.object({
  name: z.string().trim().min(1, "Give the brand a name.").max(60),
  caller_name: z.string().trim().max(15, "Caller name is limited to 15 characters."),
  messaging_profile_id: z.string().trim(),
});

function BrandDialog({ brand, onClose }: { brand: Brand | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: {
      name: brand?.name ?? "",
      caller_name: brand?.caller_name ?? "",
      messaging_profile_id: brand?.messaging_profile_id ?? "",
    },
    validators: { onSubmit: brandSchema },
    onSubmit: async ({ value }) => {
      setServerError(null);
      const body = {
        name: value.name.trim(),
        caller_name: value.caller_name.trim().toUpperCase(),
        messaging_profile_id: value.messaging_profile_id.trim() || null,
      };
      try {
        const saved = brand ? await admin.patchBrand(brand.id, body) : await admin.createBrand(body);
        qc.setQueryData<Brand[]>(qk.brands, (l) =>
          brand ? l?.map((b) => (b.id === saved.id ? saved : b)) : [...(l ?? []), saved],
        );
        toast(brand ? "Brand saved." : `${saved.name} added.`);
        onClose();
      } catch (e) {
        setServerError(errorMessage(e));
      }
    },
  });
  return (
    <Dialog open onClose={onClose} title={brand ? brand.name : "Add brand"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <form.Field name="name">
          {(f) => (
            <Field label="Name" error={firstError(f.state.meta.errors)}>
              <Input
                id="b-name"
                autoFocus
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value)}
                onBlur={f.handleBlur}
              />
            </Field>
          )}
        </form.Field>
        <form.Field name="caller_name">
          {(f) => (
            <Field
              label="Default caller name"
              error={firstError(f.state.meta.errors)}
              help="Saved as a brand setting in Kite. This does not update your phone service provider's caller-name listing."
            >
              <Input
                id="b-cnam"
                className="font-mono uppercase"
                maxLength={15}
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value)}
                onBlur={f.handleBlur}
              />
            </Field>
          )}
        </form.Field>
        <form.Field name="messaging_profile_id">
          {(f) => (
            <Field
              label="Telnyx messaging profile ID"
              help="The messaging profile tied to this brand's texting campaign."
            >
              <Input
                id="b-mp"
                className="font-mono text-caption"
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value)}
              />
            </Field>
          )}
        </form.Field>
        {serverError ? (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-ui text-danger-ink">
            {serverError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(busy) => (
              <Button type="submit" variant="primary" busy={busy}>
                {brand ? "Save" : "Add brand"}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </Dialog>
  );
}
